import React, { ChangeEvent, FormEvent, KeyboardEvent, RefObject } from 'react';
import { Send, Mic, Square, Trash2, Loader2, Camera } from 'lucide-react';
import { formatDuration, MAX_AUDIO_DURATION_MS } from '../../utils/mediaHelpers.js';

type ShortcutOption = {
  id: number;
  atalho: string;
  mensagem: string;
};

export type ConversationsComposerProps = {
  isMobile: boolean;
  onSubmit: (e: FormEvent) => void;
  showImagePanel: boolean;
  imageFile: File | null;
  onPickPhoto: () => void;
  isImageSending: boolean;
  onSendImage: () => void;
  onCancelImage: () => void;
  imageUrl: string | null;
  imageError: string | null;
  showAudioPanel: boolean;
  isRecording: boolean;
  audioBlob: Blob | null;
  onStartRecording: () => void;
  isAudioSending: boolean;
  onStopRecording: () => void;
  onSendAudio: () => void;
  onCancelAudio: () => void;
  recordingMs: number;
  audioUrl: string | null;
  audioError: string | null;
  showShortcuts: boolean;
  filteredShortcuts: ShortcutOption[];
  highlightedIndex: number;
  setNewMessage: (value: string) => void;
  setShowShortcuts: (value: boolean) => void;
  setHighlightedIndex: (value: number) => void;
  inputRef: RefObject<HTMLInputElement>;
  newMessage: string;
  onInputChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
  onToggleImagePanel: () => void;
  onToggleAudioPanel: () => void;
  fileInputRef: RefObject<HTMLInputElement>;
  onImageSelected: (e: ChangeEvent<HTMLInputElement>) => void;
};

export default function ConversationsComposer({
  isMobile,
  onSubmit,
  showImagePanel,
  imageFile,
  onPickPhoto,
  isImageSending,
  onSendImage,
  onCancelImage,
  imageUrl,
  imageError,
  showAudioPanel,
  isRecording,
  audioBlob,
  onStartRecording,
  isAudioSending,
  onStopRecording,
  onSendAudio,
  onCancelAudio,
  recordingMs,
  audioUrl,
  audioError,
  showShortcuts,
  filteredShortcuts,
  highlightedIndex,
  setNewMessage,
  setShowShortcuts,
  setHighlightedIndex,
  inputRef,
  newMessage,
  onInputChange,
  onKeyDown,
  onToggleImagePanel,
  onToggleAudioPanel,
  fileInputRef,
  onImageSelected,
}: ConversationsComposerProps) {
  return (
    <form
      onSubmit={onSubmit}
      className={`p-3 border-t border-gray-200 ${isMobile ? 'mt-auto bg-white' : ''}`}
    >
      <div className="relative mx-auto flex w-full max-w-[1600px] flex-col gap-2 md:w-[90%] xl:w-[94%]">
        {showImagePanel && (
          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center gap-2">
              {!imageFile && (
                <button
                  type="button"
                  onClick={onPickPhoto}
                  disabled={isImageSending}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200 transition disabled:opacity-50"
                >
                  <Camera size={16} />
                  Foto
                </button>
              )}
              {imageFile && (
                <>
                  <button
                    type="button"
                    onClick={onSendImage}
                    disabled={isImageSending}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition disabled:opacity-50"
                  >
                    {isImageSending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                    Enviar
                  </button>
                  <button
                    type="button"
                    onClick={onCancelImage}
                    disabled={isImageSending}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 transition disabled:opacity-50"
                  >
                    <Trash2 size={16} />
                    Cancelar
                  </button>
                </>
              )}
              {!imageFile && (
                <span className="text-xs text-slate-400">Máx: 8MB</span>
              )}
            </div>

            {imageUrl && (
              <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                <img src={imageUrl} alt="Prévia" className="max-h-40 w-auto rounded-md object-contain" />
              </div>
            )}

            {imageError && (
              <div className="text-xs text-red-500">{imageError}</div>
            )}
          </div>
        )}
        {showAudioPanel && (
          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center gap-2">
              {!isRecording && !audioBlob && (
                <button
                  type="button"
                  onClick={onStartRecording}
                  disabled={isAudioSending}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200 transition disabled:opacity-50"
                >
                  <Mic size={16} />
                  Gravar
                </button>
              )}
              {isRecording && (
                <button
                  type="button"
                  onClick={onStopRecording}
                  className="inline-flex h-9 items-center justify-center gap-2 rounded-lg bg-rose-600 px-3 text-xs font-semibold text-white transition hover:bg-rose-700"
                >
                  <Square size={16} />
                  Parar
                </button>
              )}
              {audioBlob && !isRecording && (
                <>
                  <button
                    type="button"
                    onClick={onSendAudio}
                    disabled={isAudioSending}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition disabled:opacity-50"
                  >
                    {isAudioSending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                    Enviar
                  </button>
                  <button
                    type="button"
                    onClick={onCancelAudio}
                    disabled={isAudioSending}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 transition disabled:opacity-50"
                  >
                    <Trash2 size={16} />
                    Cancelar
                  </button>
                </>
              )}
              {isRecording && (
                <span className="text-xs text-red-500 font-medium">
                  Gravando... {formatDuration(recordingMs)} / {formatDuration(MAX_AUDIO_DURATION_MS)}
                </span>
              )}
              {!isRecording && !audioBlob && (
                <span className="text-xs text-slate-400">Máx: 60s</span>
              )}
            </div>

            {audioUrl && !isRecording && (
              <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                <audio controls src={audioUrl} className="w-full" />
              </div>
            )}

            {audioError && (
              <div className="text-xs text-red-500">{audioError}</div>
            )}
          </div>
        )}
        {showShortcuts && filteredShortcuts.length > 0 && (
          <div className="absolute bottom-full mb-2 bg-white border border-gray-300 rounded-lg shadow-lg max-h-40 overflow-y-auto w-full z-10">
            {filteredShortcuts.map((shortcut, index) => (
              <div
                key={shortcut.id}
                className={`px-4 py-2 cursor-pointer ${index === highlightedIndex ? 'bg-gray-100' : 'hover:bg-gray-100'}`}
                onClick={() => {
                  setNewMessage(shortcut.mensagem);
                  setShowShortcuts(false);
                  inputRef.current?.focus();
                }}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                <span className="font-medium">/{shortcut.atalho}</span> - <span className="text-sm text-gray-500">{shortcut.mensagem.length > 50 ? shortcut.mensagem.slice(0, 50) + '...' : shortcut.mensagem}</span>
              </div>
            ))}
          </div>
        )}
        <div className="flex items-center">
          <div className="relative flex-1">
            <input
              ref={inputRef}
              type="text"
              value={newMessage}
              onChange={onInputChange}
              onKeyDown={onKeyDown}
              placeholder="Digite uma mensagem..."
              className="w-full border border-gray-300 rounded-l-full px-4 py-2 pr-16 focus:outline-none focus:ring-1 focus:ring-teal-500"
            />
            <button
              type="button"
              onClick={onToggleImagePanel}
              className={`absolute right-9 top-1/2 -translate-y-1/2 p-1 rounded-full transition ${
                showImagePanel ? 'bg-emerald-100 text-emerald-600' : 'text-gray-400 hover:text-gray-600'
              }`}
              title="Enviar imagem"
            >
              <Camera size={18} />
            </button>
            <button
              type="button"
              onClick={onToggleAudioPanel}
              className={`absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full transition ${
                showAudioPanel ? 'bg-emerald-100 text-emerald-600' : 'text-gray-400 hover:text-gray-600'
              }`}
              title="Enviar áudio"
            >
              <Mic size={18} />
            </button>
          </div>
          <button
            type="submit"
            className="flex items-center justify-center bg-teal-500 hover:bg-[#2f7d72] text-white rounded-r-full px-5 py-3 mt-0"
          >
            <Send size={18} className="-translate-x-1 rotate-45" />
          </button>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={onImageSelected}
        />
      </div>
    </form>
  );
}
